import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../../components/shared/Pagination';
import QuestionsTab from './QuestionsTab';
import ClaimsTab from './ClaimsTab';
import OrdersTab from './OrdersTab';
import PromotionsTab from './PromotionsTab';
import AdsTab from './AdsTab';
import ReputationPanel from './ReputationPanel';
import { useCan, visibleTabs, pickActiveTab, NoAccess, Can } from '../../lib/rbac';
import {
  Store, Package, MessageCircle, AlertTriangle, BarChart3,
  ExternalLink, Search, Eye,
  ShoppingCart, Globe, X, Pause, Play, XCircle, DollarSign,
  PackagePlus, RefreshCw, Loader2, Tag, Megaphone
} from 'lucide-react';
import { secureUrl } from '../../utils/url';

// ── Types ──
interface ListingsStats {
  cbt_parents: { total: number; active: number; paused: number; under_review: number; closed: number; total_stock: number; total_sold: number };
  sites: Array<{ site_id: string; total: number; active: number; paused: number; under_review: number }>;
}

interface SiteItemData {
  site_id: string;
  site_item_id: string;
  status: string;
  price: number;
  currency_id: string;
  available_quantity: number;
  sold_quantity: number;
  net_proceeds: number;
  sale_fee: number;
  shipping_cost: number;
  permalink: string;
}

interface Listing {
  item_id: string;
  seller_id: number;
  title: string;
  price: number;
  currency_id: string;
  status: string;
  sub_status: unknown;
  asin: string;
  thumbnail: string;
  available_quantity: number;
  sold_quantity: number;
  brand: string;
  permalink: string;
  last_updated: string;
  date_created: string;
  markup_override: boolean;
  markup_type: string;
  markup_value: number;
  amazon_price: number;
  amazon_current_price: number;
  manual_stock: boolean;
  site_items: SiteItemData[];
}

interface ListingDetail extends Listing {
  amazon_image: string;
  amazon_title: string;
  amazon_rating: number;
  amazon_ratings_total: number;
  keepa_monthly_sold: number;
  weight_kg: number;
  dimensions_cm: string;
  amazon_available: boolean;
}


interface SellerMetrics {
  orders: { total: number; paid: number; total_revenue: number; orders_24h: number; orders_7d: number; orders_30d: number };
  claims: { total: number; open: number; won: number; lost: number };
  listings: { total_cbt: number; active_cbt: number; total_site_items: number; active_site_items: number };
  top_sites: Array<{ site: string; orders: number; revenue: number }>;
}

interface FinancialMetrics {
  overview: { total_orders: number; total_revenue: number; total_cost: number; total_profit: number; avg_margin_pct: number };
  by_site: Array<{ site: string; orders: number; revenue: number; cost: number; profit: number; margin_pct: number }>;
  top_products: Array<{ asin: string; title: string; thumbnail: string; sales: number; revenue: number; profit: number; margin_pct: number }>;
}

// ── Site helpers ──
const SITE_FLAGS: Record<string, string> = { MLA: '🇦🇷', MLM: '🇲🇽', MLC: '🇨🇱', MCO: '🇨🇴', MLU: '🇺🇾', MLB: '🇧🇷', MLV: '🇻🇪', MPE: '🇵🇪', MEC: '🇪🇨' };
const SITE_NAMES: Record<string, string> = { MLA: 'Argentina', MLM: 'México', MLC: 'Chile', MCO: 'Colombia', MLU: 'Uruguay', MLB: 'Brasil', MLV: 'Venezuela', MPE: 'Perú', MEC: 'Ecuador' };

const STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  active: 'ok', paused: 'warning', under_review: 'info', closed: 'error',
  opened: 'warning', closed_with_resolution: 'ok',
};

const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
const fmtN = (n: number) => new Intl.NumberFormat('en-US').format(n);

const VALID_TABS = ['listings', 'orders', 'questions', 'claims', 'promotions', 'ads', 'metrics'] as const;
type MlTab = typeof VALID_TABS[number];

export default function MercadoLibrePage() {
  const { t } = useTranslation();
  const { sellerId, setSellerId } = useUIStore();
  const can = useCan();
  const [searchParams] = useSearchParams();

  const initialTab: MlTab = (() => {
    const q = searchParams.get('tab');
    return (VALID_TABS as readonly string[]).includes(q || '') ? (q as MlTab) : 'listings';
  })();
  const [tab, setTab] = useState<MlTab>(initialTab);

  useEffect(() => {
    const sellerParam = searchParams.get('seller');
    if (sellerParam) {
      const sid = parseInt(sellerParam, 10);
      if (!Number.isNaN(sid) && sid > 0 && sid !== sellerId) setSellerId(sid);
    }
    const tabParam = searchParams.get('tab');
    if (tabParam && (VALID_TABS as readonly string[]).includes(tabParam) && tabParam !== tab) {
      setTab(tabParam as MlTab);
    }
  }, [searchParams]);

  const tabs = [
    { id: 'listings' as const, label: 'Listings', icon: Package },
    { id: 'orders' as const, label: t('ml.o.orders'), icon: ShoppingCart },
    { id: 'questions' as const, label: t('ml.questions'), icon: MessageCircle },
    { id: 'claims' as const, label: t('sales.claims'), icon: AlertTriangle },
    { id: 'promotions' as const, label: t('ml.promo.tab'), icon: Tag },
    { id: 'ads' as const, label: 'Ads', icon: Megaphone },
    { id: 'metrics' as const, label: t('ml.metrics'), icon: BarChart3 },
  ];

  // Las pestañas de esta página se identifican con `id`; el mapa de capacidades usa `key`.
  const tabsWithKey = tabs.map((tb) => ({ ...tb, key: tb.id as string }));
  const allowedTabs = visibleTabs('ml', tabsWithKey, can);
  const active = pickActiveTab('ml', tabsWithKey, tab, can) as MlTab | null;
  useEffect(() => {
    if (active && active !== tab) setTab(active);
  }, [active, tab]);

  if (active === null) return <NoAccess />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Store className="w-6 h-6 text-[var(--color-primary)]" />
        <h1 className="text-xl font-bold text-[var(--color-text)]">MercadoLibre</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[var(--color-bg-secondary)] p-1 rounded-lg w-fit">
        {allowedTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              active === t.id
                ? 'bg-[var(--color-bg)] text-[var(--color-text)] shadow-sm'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {active === 'listings' && <ListingsTab sellerId={sellerId} />}
      {active === 'orders' && <OrdersTab sellerId={sellerId} />}
      {active === 'questions' && <QuestionsTab sellerId={sellerId} />}
      {active === 'claims' && <ClaimsTab sellerId={sellerId} />}
      {active === 'promotions' && <PromotionsTab sellerId={sellerId} />}
      {active === 'ads' && <AdsTab sellerId={sellerId} />}
      {active === 'metrics' && <MetricsTab sellerId={sellerId} />}
    </div>
  );
}

// ════════════════════════════════════════════════════
// LISTINGS TAB
// ════════════════════════════════════════════════════
function ListingsTab({ sellerId }: { sellerId: number }) {
  const { t } = useTranslation();
  const can = useCan();
  const canMoney = can('global.view_money');
  const canPauseActivate = can('ml.pause_activate');
  const canBulk = canPauseActivate || can('ml.close');
  const [stats, setStats] = useState<ListingsStats | null>(null);
  const [listings, setListings] = useState<Listing[]>([]);
  const pag = usePagination('mercadolibre');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [siteFilter, setSiteFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<ListingDetail | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const toggleSelect = (itemId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === listings.length) setSelected(new Set());
    else setSelected(new Set(listings.map(l => l.item_id)));
  };
  const executeBulk = async (action: string) => {
    if (action === 'close' && !window.confirm(`Close ${selected.size} listings? This cannot be undone.`)) return;
    setBulkLoading(true);
    const res = await api.post(`/mercadolibre/listings/bulk?seller_id=${sellerId}`, { action, item_ids: [...selected] });
    setBulkLoading(false);
    if (res.success) {
      const d = (res as any).data;
      alert(`${action}: ${d.success} OK, ${d.failed} failed`);
      setSelected(new Set());
      loadListings();
      loadStats();
    } else {
      alert(`Error: ${(res as any).error}`);
    }
  };

  const loadStats = useCallback(async () => {
    const res = await api.get<ListingsStats>(`/mercadolibre/stats?seller_id=${sellerId}`);
    if (res.success && res.data) setStats(res.data);
  }, [sellerId]);

  const loadListings = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({
      seller_id: String(sellerId),
      page: String(pag.page),
      per_page: String(pag.perPage),
    });
    if (search) params.set('search', search);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (siteFilter !== 'all') params.set('site', siteFilter);

    const res = await api.get<Listing[]>(`/mercadolibre/listings?${params}`);
    if (res.success && res.data) {
      setListings(res.data);
      pag.setTotal((res as any).meta?.total || 0);
    }
    setLoading(false);
  }, [sellerId, pag.page, pag.perPage, search, statusFilter, siteFilter]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadListings(); }, [loadListings]);

  const openDetail = async (itemId: string) => {
    const res = await api.get<ListingDetail>(`/mercadolibre/listings/${itemId}?seller_id=${sellerId}`);
    if (res.success && res.data) setDetail(res.data);
  };

  return (
    <div className="space-y-4">
      {/* Stats cards */}
      {stats && (
        <div className="space-y-3">
          {/* CBT Parent stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              { label: t('common.total'), value: fmtN(stats.cbt_parents.total), color: '' },
              { label: t('catalog.active'), value: fmtN(stats.cbt_parents.active), color: 'text-green-400' },
              { label: t('catalog.paused'), value: fmtN(stats.cbt_parents.paused), color: 'text-yellow-400' },
              { label: t('catalog.underReview'), value: fmtN(stats.cbt_parents.under_review), color: 'text-blue-400' },
              { label: t('catalog.closed'), value: fmtN(stats.cbt_parents.closed), color: 'text-red-400' },
              { label: t('catalog.stock'), value: fmtN(stats.cbt_parents.total_stock), color: '' },
              { label: t('catalog.totalSold'), value: fmtN(stats.cbt_parents.total_sold), color: '' },
            ].map((s, i) => (
              <div key={i} className="bg-[var(--color-bg-secondary)] rounded-lg p-3 border border-[var(--color-border)]">
                <div className="text-xs text-[var(--color-text-muted)]">{s.label}</div>
                <div className={`text-lg font-bold ${s.color || 'text-[var(--color-text)]'}`}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Site breakdown */}
          <div className="flex gap-3 flex-wrap">
            {stats.sites.map((site) => (
              <div
                key={site.site_id}
                className="bg-[var(--color-bg-secondary)] rounded-lg p-3 border border-[var(--color-border)] flex items-center gap-3 cursor-pointer hover:border-[var(--color-primary)] transition-colors"
                onClick={() => { setSiteFilter(siteFilter === site.site_id ? 'all' : site.site_id); pag.resetPage(); }}
              >
                <span className="text-xl">{SITE_FLAGS[site.site_id] || '🌍'}</span>
                <div>
                  <div className="text-xs font-medium text-[var(--color-text)]">{site.site_id}</div>
                  <div className="text-xs text-[var(--color-text-muted)]">
                    <span className="text-green-400">{site.active}</span>
                    {' / '}
                    <span className="text-yellow-400">{site.paused}</span>
                    {site.under_review > 0 && <> / <span className="text-blue-400">{site.under_review}</span></>}
                  </div>
                </div>
                <div className="text-sm font-bold text-[var(--color-text)]">{fmtN(site.total)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)]" />
          <input
            type="text"
            placeholder={t('ml.searchPlaceholder')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); pag.resetPage(); }}
            className="w-full pl-10 pr-4 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); pag.resetPage(); }}
          className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
        >
          <option value="all">{t('catalog.allProducts')}</option>
          <option value="active">{t('catalog.active')}</option>
          <option value="paused">{t('catalog.paused')}</option>
          <option value="under_review">{t('catalog.underReview')}</option>
          <option value="closed">{t('catalog.closed')}</option>
        </select>
        {siteFilter !== 'all' && (
          <button
            onClick={() => { setSiteFilter('all'); pag.resetPage(); }}
            className="flex items-center gap-1 px-3 py-2 bg-[var(--color-primary)] text-white rounded-lg text-sm"
          >
            {SITE_FLAGS[siteFilter]} {siteFilter}
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="p-3 w-8">
                {canBulk && (
                  <input type="checkbox" checked={listings.length > 0 && selected.size === listings.length} onChange={toggleAll} className="rounded" />
                )}
              </th>
              <th className="text-left p-3 text-[var(--color-text-muted)] font-medium">{t('catalog.product')}</th>
              <th className="text-left p-3 text-[var(--color-text-muted)] font-medium">{t('catalog.asin')}</th>
              <th className="text-right p-3 text-[var(--color-text-muted)] font-medium">{t('common.price')}</th>
              <th className="text-center p-3 text-[var(--color-text-muted)] font-medium">{t('common.status')}</th>
              <th className="text-center p-3 text-[var(--color-text-muted)] font-medium">{t('catalog.stock')}</th>
              <th className="text-center p-3 text-[var(--color-text-muted)] font-medium">{t('catalog.sold')}</th>
              <th className="text-center p-3 text-[var(--color-text-muted)] font-medium">{t('catalog.sites')}</th>
              <th className="text-center p-3 text-[var(--color-text-muted)] font-medium">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="p-8 text-center text-[var(--color-text-muted)]">{t('common.loading')}</td></tr>
            ) : listings.length === 0 ? (
              <tr><td colSpan={9} className="p-8 text-center text-[var(--color-text-muted)]">{t('catalog.noProducts')}</td></tr>
            ) : listings.map((item) => {
              const siteItems = typeof item.site_items === 'string' ? JSON.parse(item.site_items) : item.site_items || [];
              return (
                <tr key={item.item_id} className={`border-b border-[var(--color-border)] hover:bg-[var(--color-bg)] transition-colors ${selected.has(item.item_id) ? 'bg-[var(--color-primary)]/5' : ''}`}>
                  <td className="p-3 w-8">
                    {canBulk && (
                      <input type="checkbox" checked={selected.has(item.item_id)} onChange={() => toggleSelect(item.item_id)} className="rounded" />
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {item.thumbnail && (
                        <img src={secureUrl(item.thumbnail)} alt="" className="w-10 h-10 rounded object-cover bg-white" />
                      )}
                      <div className="min-w-0">
                        <div className="text-[var(--color-text)] font-medium">{item.title}</div>
                        {item.brand && <div className="text-xs text-[var(--color-text-muted)]">{item.brand}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-[var(--color-text)] font-mono text-xs">{item.asin}</td>
                  <td className="p-3 text-right">
                    <div className="text-[var(--color-text)] font-medium">${(item.price || 0).toFixed(2)}</div>
                    {canMoney && (item.amazon_current_price || item.amazon_price || 0) > 0 && (
                      <div className="text-xs text-[var(--color-text-muted)]">AMZ: ${(item.amazon_current_price || item.amazon_price).toFixed(2)}</div>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    <StatusBadge status={STATUS_MAP[item.status] || 'unknown'} label={item.status} size="sm" />
                  </td>
                  <td className="p-3 text-center text-[var(--color-text)]">{item.available_quantity ?? 0}</td>
                  <td className="p-3 text-center text-[var(--color-text)]">{item.sold_quantity ?? 0}</td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-center flex-wrap">
                      {siteItems.map((si: SiteItemData) => (
                        <span
                          key={si.site_item_id}
                          className={`text-xs px-1.5 py-0.5 rounded ${
                            si.status === 'active'
                              ? 'bg-green-500/20 text-green-400'
                              : si.status === 'paused'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : si.status === 'under_review'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                          title={`${si.site_id}: ${si.status} - $${(si.price || 0).toFixed(2)} ${si.currency_id || ''}`}
                        >
                          {SITE_FLAGS[si.site_id] || si.site_id}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-center">
                      {canPauseActivate && item.status === 'active' ? (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            await api.post(`/mercadolibre/listings/bulk?seller_id=${sellerId}`, { action: 'pause', item_ids: [item.item_id] });
                            loadListings();
                          }}
                          className="p-1.5 rounded hover:bg-yellow-500/20 text-[var(--color-text-muted)] hover:text-yellow-500"
                          title="Pausar en todos los sites"
                        >
                          <Pause className="w-4 h-4" />
                        </button>
                      ) : canPauseActivate && item.status === 'paused' ? (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            await api.post(`/mercadolibre/listings/bulk?seller_id=${sellerId}`, { action: 'activate', item_ids: [item.item_id] });
                            loadListings();
                          }}
                          className="p-1.5 rounded hover:bg-green-500/20 text-[var(--color-text-muted)] hover:text-green-500"
                          title="Activar en todos los sites"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                      ) : null}
                      <button
                        onClick={() => openDetail(item.item_id)}
                        className="p-1.5 rounded hover:bg-[var(--color-bg)] text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                        title={t('catalog.detail')}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {item.permalink && (
                        <a
                          href={item.permalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded hover:bg-[var(--color-bg)] text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                          title={t('catalog.viewOnML')}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Bulk Actions Bar */}
      {selected.size > 0 && canBulk && (
        <div className="flex items-center gap-3 p-3 bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/30 rounded-lg">
          <span className="text-sm font-medium text-[var(--color-text)]">
            {selected.size} {t('ml.listing.selected')}
          </span>
          <div className="flex gap-2 ml-auto">
            <Can cap="ml.pause_activate">
            <button onClick={() => executeBulk('pause')} disabled={bulkLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500/20 text-yellow-400 rounded text-sm hover:bg-yellow-500/30 disabled:opacity-50">
              {bulkLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pause className="w-3.5 h-3.5" />} {t('ml.listing.pause')}
            </button>
            <button onClick={() => executeBulk('activate')} disabled={bulkLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 text-green-400 rounded text-sm hover:bg-green-500/30 disabled:opacity-50">
              {bulkLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} {t('ml.listing.activate')}
            </button>
            </Can>
            <Can cap="ml.close">
            <button onClick={() => executeBulk('close')} disabled={bulkLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 text-red-400 rounded text-sm hover:bg-red-500/30 disabled:opacity-50">
              {bulkLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />} {t('ml.listing.close')}
            </button>
            </Can>
            <button onClick={() => setSelected(new Set())}
              className="flex items-center gap-1 px-3 py-1.5 text-[var(--color-text-muted)] rounded text-sm hover:bg-[var(--color-bg-secondary)]">
              <X className="w-3.5 h-3.5" /> {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Pagination */}
      <Pagination
        page={pag.page}
        totalPages={pag.totalPages}
        total={pag.total}
        perPage={pag.perPage}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />

      {/* Detail Panel */}
      {detail && <ListingDetailPanel item={detail} sellerId={sellerId} onClose={() => setDetail(null)} onRefresh={() => { loadListings(); if (detail) openDetail(detail.item_id); }} />}
    </div>
  );
}

// ── Listing Detail Slide Panel ──
function ListingDetailPanel({ item, sellerId, onClose, onRefresh }: { item: ListingDetail; sellerId: number; onClose: () => void; onRefresh: () => void }) {
  const { t } = useTranslation();
  const can = useCan();
  const canMoney = can('global.view_money');
  const hasActions = can('ml.pause_activate') || can('ml.close') || can('catalog.edit_price');
  const siteItems = item.site_items || [];
  const [actionLoading, setActionLoading] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [showReprice, setShowReprice] = useState(false);

  const executeAction = async (action: string, body?: Record<string, unknown>) => {
    const confirmActions: Record<string, string> = { close: 'Are you sure you want to CLOSE this listing? This cannot be undone.' };
    if (confirmActions[action] && !window.confirm(confirmActions[action])) return;

    setActionLoading(action);
    const res = await api.post(`/mercadolibre/listings/items/${item.item_id}/${action}?seller_id=${sellerId}`, body || {});
    if (res.success) {
      onRefresh();
    } else {
      alert(`Error: ${(res as any).error || 'Unknown error'}`);
    }
    setActionLoading('');
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[var(--color-bg)] shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-[var(--color-bg)] border-b border-[var(--color-border)] p-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-bold text-[var(--color-text)]">{t('catalog.detail')}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-bg-secondary)]">
            <X className="w-5 h-5 text-[var(--color-text-muted)]" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Header with image */}
          <div className="flex gap-4">
            <img
              src={secureUrl(item.amazon_image || item.thumbnail || '')}
              alt=""
              className="w-32 h-32 rounded-lg object-cover bg-white flex-shrink-0"
            />
            <div className="min-w-0 space-y-1">
              <h3 className="text-[var(--color-text)] font-medium leading-tight">{item.title}</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono bg-[var(--color-bg-secondary)] px-2 py-0.5 rounded text-[var(--color-text-muted)]">
                  {item.asin}
                </span>
                <span className="text-xs font-mono bg-[var(--color-bg-secondary)] px-2 py-0.5 rounded text-[var(--color-text-muted)]">
                  {item.item_id}
                </span>
                <StatusBadge status={STATUS_MAP[item.status] || 'unknown'} label={item.status} size="sm" />
              </div>
              {item.brand && <div className="text-sm text-[var(--color-text-muted)]">{item.brand}</div>}
              <div className="flex gap-4 text-sm">
                <span className="text-[var(--color-text)]">CBT: <strong>${(item.price || 0).toFixed(2)}</strong></span>
                {canMoney && (item.amazon_current_price || item.amazon_price || 0) > 0 && (
                  <span className="text-[var(--color-text-muted)]">AMZ: ${(item.amazon_current_price || item.amazon_price).toFixed(2)}</span>
                )}
              </div>
            </div>
          </div>

          {/* Pricing & Stock info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <InfoCard label={t('catalog.stock')} value={String(item.available_quantity ?? 0)} />
            <InfoCard label={t('catalog.sold')} value={fmtN(item.sold_quantity ?? 0)} />
            {canMoney && item.markup_override && (
              <InfoCard label={t('catalog.markup')} value={`${item.markup_value}${item.markup_type === 'percentage' ? '%' : '$'}`} />
            )}
            {item.manual_stock && <InfoCard label={t('catalog.manualStock')} value="ON" />}
          </div>

          {/* Amazon data */}
          {item.amazon_rating > 0 && (
            <div className="bg-[var(--color-bg-secondary)] rounded-lg p-3 border border-[var(--color-border)]">
              <h4 className="text-sm font-medium text-[var(--color-text)] mb-2">{t('catalog.amazonData')}</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div>
                  <span className="text-[var(--color-text-muted)]">{t('catalog.rating')}: </span>
                  <span className="text-[var(--color-text)]">{'★'.repeat(Math.round(item.amazon_rating))} {item.amazon_rating}</span>
                </div>
                <div>
                  <span className="text-[var(--color-text-muted)]">{t('catalog.reviews')}: </span>
                  <span className="text-[var(--color-text)]">{fmtN(item.amazon_ratings_total || 0)}</span>
                </div>
                {item.keepa_monthly_sold > 0 && (
                  <div>
                    <span className="text-[var(--color-text-muted)]">{t('catalog.monthlySold')}: </span>
                    <span className="text-[var(--color-text)]">{fmtN(item.keepa_monthly_sold)}</span>
                  </div>
                )}
                {item.weight_kg > 0 && (
                  <div>
                    <span className="text-[var(--color-text-muted)]">{t('catalog.weight')}: </span>
                    <span className="text-[var(--color-text)]">{item.weight_kg} kg</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Site Items table */}
          <div>
            <h4 className="text-sm font-medium text-[var(--color-text)] mb-2">{t('catalog.siteItems')} ({siteItems.length})</h4>
            {siteItems.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)]">{t('catalog.noSiteItems')}</p>
            ) : (
              <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      <th className="text-left p-2 text-[var(--color-text-muted)]">{t('sales.site')}</th>
                      <th className="text-center p-2 text-[var(--color-text-muted)]">{t('common.status')}</th>
                      <th className="text-right p-2 text-[var(--color-text-muted)]">{t('common.price')}</th>
                      <th className="text-right p-2 text-[var(--color-text-muted)]">{t('catalog.netProceeds')}</th>
                      <th className="text-right p-2 text-[var(--color-text-muted)]">{t('catalog.saleFee')}</th>
                      <th className="text-center p-2 text-[var(--color-text-muted)]">{t('catalog.sold')}</th>
                      <th className="text-center p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {siteItems.map((si: any) => (
                      <tr key={si.site_item_id} className="border-b border-[var(--color-border)]">
                        <td className="p-2">
                          <span className="mr-1">{SITE_FLAGS[si.site_id]}</span>
                          <span className="text-[var(--color-text)]">{si.site_id}</span>
                        </td>
                        <td className="p-2 text-center">
                          <StatusBadge status={STATUS_MAP[si.status] || 'unknown'} label={si.status} size="sm" />
                        </td>
                        <td className="p-2 text-right text-[var(--color-text)]">
                          ${(si.price || 0).toFixed(2)} <span className="text-xs text-[var(--color-text-muted)]">{si.currency_id}</span>
                        </td>
                        <td className="p-2 text-right text-[var(--color-text)]">
                          {canMoney && si.net_proceeds ? `$${si.net_proceeds.toFixed(2)}` : '\u2014'}
                        </td>
                        <td className="p-2 text-right text-[var(--color-text-muted)]">
                          {canMoney && si.sale_fee ? `$${si.sale_fee.toFixed(2)}` : '\u2014'}
                        </td>
                        <td className="p-2 text-center text-[var(--color-text)]">{si.sold_quantity ?? 0}</td>
                        <td className="p-2 text-center">
                          {si.permalink && (
                            <a href={si.permalink} target="_blank" rel="noopener noreferrer" className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Actions — cada botón pide su capacidad; sin ninguna, no se muestra el bloque ── */}
          {hasActions && (
          <div className="bg-[var(--color-bg-secondary)] rounded-lg p-3 border border-[var(--color-border)] space-y-3">
            <h4 className="text-sm font-medium text-[var(--color-text)]">{t('ml.listing.actions')}</h4>
            <div className="flex gap-2 flex-wrap">
              {can('ml.pause_activate') && item.status === 'active' && (
                <button
                  onClick={() => executeAction('pause')}
                  disabled={!!actionLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500/20 text-yellow-400 rounded-lg text-sm hover:bg-yellow-500/30 disabled:opacity-50"
                >
                  {actionLoading === 'pause' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pause className="w-3.5 h-3.5" />}
                  {t('ml.listing.pause')}
                </button>
              )}
              {can('ml.pause_activate') && item.status === 'paused' && (
                <button
                  onClick={() => executeAction('activate')}
                  disabled={!!actionLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-sm hover:bg-green-500/30 disabled:opacity-50"
                >
                  {actionLoading === 'activate' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                  {t('ml.listing.activate')}
                </button>
              )}
              {can('ml.close') && item.status !== 'closed' && (
                <button
                  onClick={() => executeAction('close')}
                  disabled={!!actionLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30 disabled:opacity-50"
                >
                  {actionLoading === 'close' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                  {t('ml.listing.close')}
                </button>
              )}
              {can('catalog.edit_price') && item.status !== 'closed' && (
                <button
                  onClick={() => setShowReprice(!showReprice)}
                  disabled={!!actionLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-sm hover:bg-blue-500/30 disabled:opacity-50"
                >
                  <DollarSign className="w-3.5 h-3.5" />
                  {t('ml.listing.reprice')}
                </button>
              )}
              {can('ml.pause_activate') && item.status !== 'closed' && (
                <button
                  onClick={() => executeAction('refresh')}
                  disabled={!!actionLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg text-sm hover:bg-purple-500/30 disabled:opacity-50"
                >
                  {actionLoading === 'refresh' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  {t('ml.listing.refresh')}
                </button>
              )}
            </div>

            {/* Reprice input */}
            {showReprice && (
              <div className="flex items-center gap-2 pt-1">
                <span className="text-sm text-[var(--color-text-muted)]">{t('ml.listing.newPrice')}:</span>
                <span className="text-sm text-[var(--color-text-muted)]">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  placeholder={canMoney ? String(item.price || 0) : ''}
                  className="w-28 px-2 py-1 text-sm rounded bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] focus:border-[var(--color-primary)] outline-none"
                />
                <button
                  onClick={() => {
                    if (!newPrice || Number(newPrice) <= 0) { alert('Invalid price'); return; }
                    executeAction('reprice', { price: Number(newPrice) });
                    setShowReprice(false);
                    setNewPrice('');
                  }}
                  disabled={!!actionLoading || !newPrice}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {actionLoading === 'reprice' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('ml.listing.applyPrice')}
                </button>
              </div>
            )}
          </div>
          )}

          {/* Links */}
          <div className="flex gap-2 flex-wrap">
            {item.permalink && (
              <a href={item.permalink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1.5 bg-yellow-500/20 text-yellow-400 rounded-lg text-sm hover:bg-yellow-500/30">
                <ExternalLink className="w-3.5 h-3.5" /> {t('catalog.viewOnML')}
              </a>
            )}
            {item.asin && (
              <a href={`https://www.amazon.com/dp/${item.asin}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1.5 bg-orange-500/20 text-orange-400 rounded-lg text-sm hover:bg-orange-500/30">
                <ExternalLink className="w-3.5 h-3.5" /> {t('catalog.viewOnAmazon')}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════
// CLAIMS TAB
// ════════════════════════════════════════════════════
// ClaimsTab moved to ./ClaimsTab.tsx

// ════════════════════════════════════════════════════
// METRICS TAB
// ════════════════════════════════════════════════════
function MetricsTab({ sellerId }: { sellerId: number }) {
  const { t } = useTranslation();
  // Sin la llave del dinero, los bloques de plata no se muestran.
  const canMoney = useCan()('global.view_money');
  const [metrics, setMetrics] = useState<SellerMetrics | null>(null);
  const [financials, setFinancials] = useState<FinancialMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [mRes, fRes] = await Promise.all([
        api.get<SellerMetrics>(`/mercadolibre/metrics?seller_id=${sellerId}`),
        api.get<FinancialMetrics>(`/mercadolibre/financials?seller_id=${sellerId}`),
      ]);
      if (mRes.success && mRes.data) setMetrics(mRes.data);
      if (fRes.success && fRes.data) setFinancials(fRes.data);
      setLoading(false);
    })();
  }, [sellerId]);

  if (loading) return <div className="p-8 text-center text-[var(--color-text-muted)]">{t('common.loading')}</div>;
  if (!metrics) return <div className="p-8 text-center text-[var(--color-text-muted)]">{t('common.noData')}</div>;

  return (
    <div className="space-y-6">
      {/* Seller Reputation (live from ML API) */}
      <ReputationPanel sellerId={sellerId} />

      {/* Financial Overview */}
      {canMoney && financials && financials.overview.total_orders > 0 && (
        <div>
          <h3 className="text-sm font-medium text-[var(--color-text)] mb-3 flex items-center gap-2">
            <DollarSign className="w-4 h-4" /> {t('ml.fin.overview')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <MetricCard label={t('ml.fin.revenue')} value={fmt(financials.overview.total_revenue)} color="text-blue-400" />
            <MetricCard label={t('ml.fin.cost')} value={fmt(financials.overview.total_cost)} color="text-orange-400" />
            <MetricCard label={t('ml.fin.profit')} value={fmt(financials.overview.total_profit)} color={financials.overview.total_profit >= 0 ? 'text-green-400' : 'text-red-400'} />
            <MetricCard label={t('ml.fin.margin')} value={`${financials.overview.avg_margin_pct}%`} color={financials.overview.avg_margin_pct >= 20 ? 'text-green-400' : 'text-yellow-400'} />
            <MetricCard label={t('ml.fin.ordersWithCost')} value={fmtN(financials.overview.total_orders)} />
          </div>
        </div>
      )}

      {/* Orders metrics */}
      <div>
        <h3 className="text-sm font-medium text-[var(--color-text)] mb-3 flex items-center gap-2">
          <ShoppingCart className="w-4 h-4" /> {t('sales.orders')}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <MetricCard label={t('common.total')} value={fmtN(metrics.orders.total)} />
          <MetricCard label={t('sales.paid')} value={fmtN(metrics.orders.paid)} />
          {canMoney && <MetricCard label={t('finance.revenue')} value={fmt(metrics.orders.total_revenue)} color="text-green-400" />}
          <MetricCard label={t('ml.orders24h')} value={fmtN(metrics.orders.orders_24h)} />
          <MetricCard label={t('ml.orders7d')} value={fmtN(metrics.orders.orders_7d)} />
          <MetricCard label={t('ml.orders30d')} value={fmtN(metrics.orders.orders_30d)} />
        </div>
      </div>

      {/* Listings metrics */}
      <div>
        <h3 className="text-sm font-medium text-[var(--color-text)] mb-3 flex items-center gap-2">
          <Package className="w-4 h-4" /> Listings
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard label="CBT Parents" value={fmtN(metrics.listings.total_cbt)} />
          <MetricCard label={t('ml.activeCBT')} value={fmtN(metrics.listings.active_cbt)} color="text-green-400" />
          <MetricCard label="Site Items" value={fmtN(metrics.listings.total_site_items)} />
          <MetricCard label={t('ml.activeSiteItems')} value={fmtN(metrics.listings.active_site_items)} color="text-green-400" />
        </div>
      </div>

      {/* Claims metrics */}
      <div>
        <h3 className="text-sm font-medium text-[var(--color-text)] mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {t('sales.claims')}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard label={t('common.total')} value={fmtN(metrics.claims.total)} />
          <MetricCard label={t('ml.claimOpen')} value={fmtN(metrics.claims.open)} color={metrics.claims.open > 0 ? 'text-yellow-400' : undefined} />
          <MetricCard label={t('ml.claimWon')} value={fmtN(metrics.claims.won)} color="text-green-400" />
          <MetricCard label={t('ml.claimLost')} value={fmtN(metrics.claims.lost)} color="text-red-400" />
        </div>
      </div>

      {/* Profitability by Site (30d) */}
      {canMoney && financials && financials.by_site.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-[var(--color-text)] mb-3 flex items-center gap-2">
            <Globe className="w-4 h-4" /> {t('ml.fin.profitBySite')} ({t('ml.last30d')})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {financials.by_site.map((site) => (
              <div key={site.site} className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">{SITE_FLAGS[site.site] || '🌍'}</span>
                  <span className="text-sm font-medium text-[var(--color-text)]">{SITE_NAMES[site.site] || site.site}</span>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[var(--color-text-muted)]">{t('ml.fin.revenue')}</span>
                    <span className="text-blue-400 font-medium">{fmt(site.revenue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--color-text-muted)]">{t('ml.fin.cost')}</span>
                    <span className="text-orange-400">{fmt(site.cost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--color-text-muted)]">{t('ml.fin.profit')}</span>
                    <span className={`font-bold ${site.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{fmt(site.profit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--color-text-muted)]">{t('ml.fin.margin')}</span>
                    <span className={site.margin_pct >= 20 ? 'text-green-400' : 'text-yellow-400'}>{site.margin_pct}%</span>
                  </div>
                  <div className="text-xs text-[var(--color-text-muted)] text-right">{fmtN(site.orders)} {t('finance.orders').toLowerCase()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Sites (revenue only, for sellers without cost data) */}
      {canMoney && (!financials || financials.by_site.length === 0) && metrics.top_sites.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-[var(--color-text)] mb-3 flex items-center gap-2">
            <Globe className="w-4 h-4" /> {t('finance.bySite')} ({t('ml.last30d')})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {metrics.top_sites.map((site) => (
              <div key={site.site} className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4 text-center">
                <span className="text-2xl">{SITE_FLAGS[site.site] || '🌍'}</span>
                <div className="text-sm font-medium text-[var(--color-text)] mt-1">{SITE_NAMES[site.site] || site.site}</div>
                <div className="text-lg font-bold text-green-400 mt-1">{fmt(site.revenue)}</div>
                <div className="text-xs text-[var(--color-text-muted)]">{fmtN(site.orders)} {t('finance.orders').toLowerCase()}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Site Item Visits (live) */}
      <VisitsPanel sellerId={sellerId} />

      {/* Top Products by Profit (30d) */}
      {canMoney && financials && financials.top_products.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-[var(--color-text)] mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> {t('ml.fin.topProducts')} ({t('ml.last30d')})
          </h3>
          <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left p-3 text-[var(--color-text-muted)]">{t('catalog.product')}</th>
                  <th className="text-center p-3 text-[var(--color-text-muted)]">{t('catalog.sold')}</th>
                  <th className="text-right p-3 text-[var(--color-text-muted)]">{t('ml.fin.revenue')}</th>
                  <th className="text-right p-3 text-[var(--color-text-muted)]">{t('ml.fin.profit')}</th>
                  <th className="text-right p-3 text-[var(--color-text-muted)]">{t('ml.fin.margin')}</th>
                </tr>
              </thead>
              <tbody>
                {financials.top_products.map((p, i) => (
                  <tr key={p.asin || i} className="border-b border-[var(--color-border)]">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {p.thumbnail && <img src={secureUrl(p.thumbnail)} alt="" className="w-8 h-8 rounded object-cover bg-white" />}
                        <div className="min-w-0">
                          <div className="text-[var(--color-text)]">{p.title}</div>
                          <div className="text-xs text-[var(--color-text-muted)] font-mono">{p.asin}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-center text-[var(--color-text)]">{p.sales}</td>
                    <td className="p-3 text-right text-blue-400">{fmt(p.revenue)}</td>
                    <td className="p-3 text-right">
                      <span className={p.profit >= 0 ? 'text-green-400' : 'text-red-400'}>{fmt(p.profit)}</span>
                    </td>
                    <td className="p-3 text-right">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        p.margin_pct >= 25 ? 'bg-green-500/20 text-green-400' :
                        p.margin_pct >= 15 ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>{p.margin_pct}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Visits Panel ──
interface VisitsData {
  item_id: string;
  total_visits: number;
  total_in_range: number;
  avg_daily: number;
  days: number;
  daily: Array<{ date: string; visits: number }>;
}

function VisitsPanel({ sellerId }: { sellerId: number }) {
  const { t } = useTranslation();
  const [topItems, setTopItems] = useState<Array<{ site_item_id: string; site_id: string; visits: number }>>([]);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [visitsDetail, setVisitsDetail] = useState<VisitsData | null>(null);
  const [loadingTop, setLoadingTop] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await api.get<any>(`/mercadolibre/metrics-live/visits?seller_id=${sellerId}&limit=10`);
      if (res.success && res.data) {
        setTopItems(res.data);
      }
      setLoadingTop(false);
    })();
  }, [sellerId]);

  const loadVisitsDetail = useCallback(async (itemId: string) => {
    setSelectedItem(itemId);
    setLoadingDetail(true);
    const res = await api.get<VisitsData>(`/mercadolibre/metrics-live/visits/detail/${itemId}?seller_id=${sellerId}`);
    if (res.success && res.data) {
      setVisitsDetail(res.data as unknown as VisitsData);
    }
    setLoadingDetail(false);
  }, [sellerId]);

  if (loadingTop) return null;
  if (topItems.length === 0) return null;

  const maxVisits = visitsDetail ? Math.max(...visitsDetail.daily.map(d => d.visits), 1) : 1;

  return (
    <div>
      <h3 className="text-sm font-medium text-[var(--color-text)] mb-3 flex items-center gap-2">
        <Eye className="w-4 h-4" /> {t('ml.visits.title')}
      </h3>

      {/* Top items with visits */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
        {topItems.map((item) => (
          <button
            key={item.site_item_id}
            onClick={() => loadVisitsDetail(item.site_item_id)}
            className={`text-left bg-[var(--color-bg-secondary)] rounded-lg border p-3 transition-colors ${
              selectedItem === item.site_item_id
                ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]'
                : 'border-[var(--color-border)] hover:border-[var(--color-text-muted)]'
            }`}
          >
            <div className="text-xs text-[var(--color-text-muted)] font-mono truncate">{item.site_item_id}</div>
            <div className="text-sm font-bold text-[var(--color-text)]">{fmtN(item.visits)}</div>
            <div className="text-[10px] text-[var(--color-text-muted)]">{t('ml.visits.totalVisits')}</div>
          </button>
        ))}
      </div>

      {/* Daily visits chart */}
      {selectedItem && (
        <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
          {loadingDetail ? (
            <div className="flex items-center justify-center h-32 text-sm text-[var(--color-text-muted)]">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('common.loading')}
            </div>
          ) : visitsDetail ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm font-medium text-[var(--color-text)]">
                  {visitsDetail.item_id} — {t('ml.visits.last30d')}
                </div>
                <div className="flex gap-4 text-xs">
                  <span className="text-[var(--color-text-muted)]">
                    {t('ml.visits.totalVisits')}: <span className="text-[var(--color-text)] font-bold">{fmtN(visitsDetail.total_visits)}</span>
                  </span>
                  <span className="text-[var(--color-text-muted)]">
                    {t('ml.visits.inRange')}: <span className="text-[var(--color-text)] font-bold">{fmtN(visitsDetail.total_in_range)}</span>
                  </span>
                  <span className="text-[var(--color-text-muted)]">
                    {t('ml.visits.avgDaily')}: <span className="text-[var(--color-text)] font-bold">{fmtN(visitsDetail.avg_daily)}</span>
                  </span>
                </div>
              </div>
              {/* Bar chart */}
              <div className="flex items-end gap-[2px] h-32">
                {visitsDetail.daily.map((day, i) => {
                  const height = maxVisits > 0 ? (day.visits / maxVisits) * 100 : 0;
                  const dateStr = new Date(day.date).toLocaleDateString('es', { day: '2-digit', month: '2-digit' });
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center group relative">
                      <div
                        className="w-full bg-[var(--color-primary)] rounded-t-sm hover:bg-[var(--color-primary-hover)] transition-colors min-h-[2px]"
                        style={{ height: `${Math.max(height, 1.5)}%` }}
                        title={`${dateStr}: ${day.visits} visitas`}
                      />
                      {/* Tooltip on hover */}
                      <div className="absolute -top-8 hidden group-hover:block bg-[var(--color-surface)] border border-[var(--color-border)] rounded px-2 py-1 text-[10px] text-[var(--color-text)] whitespace-nowrap shadow-lg z-10">
                        {dateStr}: {day.visits}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-1 text-[10px] text-[var(--color-text-muted)]">
                {visitsDetail.daily.length > 0 && (
                  <>
                    <span>{new Date(visitsDetail.daily[0].date).toLocaleDateString('es', { day: '2-digit', month: '2-digit' })}</span>
                    <span>{new Date(visitsDetail.daily[visitsDetail.daily.length - 1].date).toLocaleDateString('es', { day: '2-digit', month: '2-digit' })}</span>
                  </>
                )}
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ── Shared components ──
function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-[var(--color-bg-secondary)] rounded-lg p-3 border border-[var(--color-border)]">
      <div className="text-xs text-[var(--color-text-muted)]">{label}</div>
      <div className="text-lg font-bold text-[var(--color-text)]">{value}</div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="bg-[var(--color-bg-secondary)] rounded-lg p-4 border border-[var(--color-border)]">
      <div className="text-xs text-[var(--color-text-muted)] mb-1">{label}</div>
      <div className={`text-xl font-bold ${color || 'text-[var(--color-text)]'}`}>{value}</div>
    </div>
  );
}

